import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { fontWeights } from '../../theme/type';
import { headers, body } from '../../styles/type';

import Button from '../buttons/Button';
import ButtonText from '../buttons/ButtonText';
import TextRenderer from '../TextRenderer';
import VisuallyHidden from '../VisuallyHidden';
import { ReactComponent as Caret } from '../../images/icons/caret-down.svg';

const FeaturesTableWrapper = styled.div`
  position: relative;
`;

const FeaturesTable = styled.table`
  position: relative;
  z-index: 0;
  width: 100%;
  border-collapse: collapse;

  ${Button},
  ${ButtonText} {
    font-size: ${rem(16)};
    white-space: nowrap;
  }
`;

const Head = styled.thead`
  position: sticky;
  z-index: 1;
  top: ${rem(90)};
  bottom: ${rem(150)};
  background: ${colors.white};
`;

const FeatureLabels = styled.tr`
`;

const FeatureLabel = styled.th`
  ${headers.eyebrow}
  border-bottom: solid 1px ${colors.gray['200']};
  padding: ${rem(12)} ${rem(8)};
  text-align: center;
  vertical-align: bottom;
  color: ${colors.blue['800']};
`;

const Body = styled.tbody`

`;

const Row = styled.tr`
  transition: all 0.3s ease;
  ${({ hidden }) => hidden && `
    display: none;
  `}
`;

const Feature = styled.td`
  ${body.regular}
  padding: ${rem(16)} 0;
  border-bottom: solid 1px ${colors.gray['200']};
  text-align: left;
  vertical-align: top;
  font-weight: ${fontWeights.regular};
  color: ${colors.gray['600']};
`;

const Column = styled.td`
  border-bottom: solid 1px ${colors.gray['200']};
`;

const FeatureAvailability = styled.div`
  width: ${rem(12)};
  height: ${rem(12)};
  margin: auto;
  border-radius: 50%;
  background: ${({ available }) => (available ? colors.blue['500'] : colors.blue['50'])};
`;

const FeatureText = styled.div`
  text-align: center;
  font-weight: ${fontWeights.semiBold};
  color: ${colors.blue['500']};
`;

const Foot = styled.tfoot`

`;

const CtaColumn = styled.td`
  padding: ${rem(24)} ${rem(8)} 0;
  text-align: center;
`;

const ExpandButton = styled.button`
  margin-top: auto;
  cursor: pointer;
  text-transform: uppercase;
  background: none;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-self: center;

  svg {
    color: ${colors.blue['500']};
  }
`;

const ExpandButtonWrapper = styled.div`
  display: grid;
  position: relative;
  height: ${rem(150)};
  margin-top: ${rem(-150)};
  background: linear-gradient(rgba(255, 255, 255, .65), rgba(255, 255, 255, 1));
`;

const FeaturesTableHorizontal = ({
  className,
  tableData,
  tableHeading,
  expandListText,
  showExpandListButton,
}) => {
  const [showMore, setShowMore] = useState(false);
  const visibleRows = 10;

  return (
    <FeaturesTableWrapper className={className}>
      <FeaturesTable>
        <Head>
          <FeatureLabels>
            <FeatureLabel width="45%">
              {tableHeading && (
                <VisuallyHidden>
                  <TextRenderer text={tableHeading} />
                </VisuallyHidden>
              )}
            </FeatureLabel>
            {tableData.columns.map((column, i) => {
              const width = Math.min(55 / tableData.columns.length, 20);

              return (
                // eslint-disable-next-line react/no-array-index-key
                <FeatureLabel key={i} width={`${width}%`}>
                  <TextRenderer text={column} />
                </FeatureLabel>
              );
            })}
          </FeatureLabels>
        </Head>
        <Body>
          {tableData.rows.map((row, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Row key={i} hidden={i >= visibleRows && !showMore && showExpandListButton}>
              <Feature>
                <TextRenderer text={row} />
              </Feature>
              {tableData.values[i].map((value, j) => (
                // eslint-disable-next-line react/no-array-index-key
                <Column key={j}>
                  {typeof value === 'string' ? (
                    <FeatureText>
                      <TextRenderer text={value} />
                    </FeatureText>
                  ) : (
                    <FeatureAvailability available={value} />
                  )}
                </Column>
              ))}
            </Row>
          ))}
        </Body>
        <Foot>
          <Row>
            <CtaColumn />
            {tableData.links.map((link, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <CtaColumn key={index}>
                {link.primary ? (
                  <Button to={link.url}>
                    <TextRenderer text={link.title} />
                  </Button>
                ) : (
                  <ButtonText to={link.url}>
                    <TextRenderer text={link.title} />
                  </ButtonText>
                )}
              </CtaColumn>
            ))}
          </Row>
        </Foot>
      </FeaturesTable>
      {!showMore && showExpandListButton && (
        <ExpandButtonWrapper>
          <ExpandButton onClick={() => setShowMore(true)}>
            <TextRenderer text={expandListText} />
            <Caret />
          </ExpandButton>
        </ExpandButtonWrapper>
      )}
    </FeaturesTableWrapper>
  );
};

FeaturesTableHorizontal.propTypes = {
  className: PropTypes.string,
  tableData: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.string),
    rows: PropTypes.arrayOf(PropTypes.string),
    values: PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number])
      )
    ),
    links: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
      primary: PropTypes.bool
    }))
  }),
  expandListText: PropTypes.string,
  showExpandListButton: PropTypes.bool,
};

export default FeaturesTableHorizontal;
